import { Injectable, inject } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { DeviceService, Device as GatewayDevice } from '@era/gateway-sdk';
import { DeviceType, DevicesService } from '@rocksoft/mdm-sdk-v2';

import { Device } from '@rocksoft/shared';
import { safeMap } from '../effects.helpers';
import {
  UnenrolledDeviceApiActions,
  UnenrolledDeviceResolverActions,
  UnenrolledDeviceViewActions,
} from './unenrolled-device.actions';

const GATEWAY_DEVICE_TYPE_MAP: Record<number, DeviceType> = {
  1: DeviceType.Apple,
  2: DeviceType.Android,
  3: DeviceType.Windows,
};

function mapGatewayDevice(d: GatewayDevice): Device {
  return {
    udid: d.identifier ?? '',
    name: d.displayName ?? null,
    platform: GATEWAY_DEVICE_TYPE_MAP[d.type ?? 0],
    serialNumber: d.serialNumber ?? null,
    lastSeen: d.lastSeen ?? null,
  };
}

@Injectable()
export class UnenrolledDeviceEffects {
  private readonly deviceService = inject(DeviceService);
  private readonly devicesService = inject(DevicesService);
  private readonly actions$ = inject(Actions);

  appleDeviceLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UnenrolledDeviceResolverActions.unenrolledDevicesRequested),
      mergeMap(() =>
        safeMap(
          this.deviceService.getUnenrolledDevices(),
          (unenrolledDevices) =>
            UnenrolledDeviceApiActions.unenrolledDevicesLoaded({
              unenrolledDevices: unenrolledDevices.map(mapGatewayDevice),
            }),
          (error) => UnenrolledDeviceApiActions.unenrolledDevicesLoadedError({ error })
        )
      )
    );
  });

  unenrolledDevicesDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UnenrolledDeviceViewActions.unenrolledDevicesDeleteRequested),
      mergeMap(({ unenrolledDeviceIds }) =>
        safeMap(
          this.devicesService.deleteDevices({ apiVersion: '2.0', requestBody: unenrolledDeviceIds }),
          () => UnenrolledDeviceViewActions.unenrolledDevicesDeleted(),
          (error) => UnenrolledDeviceApiActions.unenrolledDevicesDeletedError({ error })
        )
      )
    );
  });
}
